import { useEffect, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loader from 'shared/containers/Loader';
import { SETTINGS_TITLE } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT } from 'shared/models/api';
import { SourcesResultT } from 'shared/models/sources';
import SourcesForm, { SourcesCommonFields, SourcesIdentifiers } from 'shared/models/sources-form';
import api from 'utils/api';

import './SourcesManageDataForm.scss';

import { FormValuesT } from '../SourcesManageData';
import SourcesFormFieldRender from './components/SourcesFormFieldRender';

type SourcesManageDataFormProps = {
  name: string;
  currentValues: SourcesResultT | null;
}

function SourcesManageDataForm({ name, currentValues }: SourcesManageDataFormProps) {
  const params: any = useParams();
  const isEdit = params.id !== undefined;
  const [isLoading, setIsLoading] = useState(true);
  const form = SourcesForm[name];
  const { reset } = useFormContext();

  /* CREATE EDIT MODE */
  const initDefaultValuesForm = (defaultObject: any) => {
    const initState: FormValuesT = {
      sourcePath: defaultObject.sourcePath,
      specific: {},
    };
    form.forEach((field) => {
      let fieldName = field.name;
      if (fieldName.includes('.')) {
        [fieldName] = fieldName.split('.', 1);
      }
      const fieldValue = defaultObject[fieldName];
      (initState.specific as any)[fieldName] = fieldValue;
    });
    reset(initState);
    setIsLoading(false);
  };

  const getDefaultValuesForm = (type: string) => {
    api.sources.getUrl(type)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setIsLoading(false);
            });
        } else {
          resp.json()
            .then((data: any) => {
              initDefaultValuesForm(data);
            });
        }
      });
  };

  const createMode = () => {
    const noDefaultDataFromAPISourcesList = [
      'ACKLIO_LNS_WS_SOURCE',
      'INNOVID_SOURCE',
      'CHIRPSTACK_MULTICAST_SOURCE',
      'ANTARES_SOURCE',
      'EVERYNET_SOURCE',
      'NBIOT_SCEF_SOURCE',
      'ORANGE_LIVEOBJECTS_SOURCE',
    ];
    if (!noDefaultDataFromAPISourcesList.includes(name) && form) {
      getDefaultValuesForm(name);
      return;
    }
    if (!form) {
      toast.warning('Form undefined');
    }
    reset({
      specific: {},
    });
    setIsLoading(false);
  };

  /* EDIT MODE */
  const initExistingValuesEditMode = (values: any) => {
    const sourcesIdentifier = SourcesIdentifiers[values?.source];
    let parametersGrouped: any = {};
    if (sourcesIdentifier) {
      parametersGrouped = values[sourcesIdentifier.parametersGroupName];
    }
    const resetState = {
      ...values,
      specific: parametersGrouped,
    } as FormValuesT;
    reset(resetState);
    setIsLoading(false);
  };

  const editMode = () => {
    initExistingValuesEditMode(currentValues);
  };

  useEffect(() => {
    if (!isEdit) {
      createMode();
    } else {
      editMode();
    }
  }, [name, currentValues]);

  if (isLoading) {
    return <Loader.Section />;
  }
  if (!form) {
    return (
      <div className="position-center">
        <h3>Form undefined</h3>
      </div>
    );
  }
  return (
    <div className="form__section">
      <h3>{SETTINGS_TITLE}</h3>
      {
        SourcesCommonFields.map((field) => (
          <SourcesFormFieldRender field={field} key={`common.${field.name}.parent`} />))
      }
      {
        form?.map((field) => (
          <SourcesFormFieldRender field={field} isSpecific key={`specific.${field.name}.parent`} />))
      }
    </div>
  );
}

export default SourcesManageDataForm;
